import { App } from 'antd';
import type { CSSProperties, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { useBusinessContext } from '../context/BusinessContext';
import { ModalSelect } from './ModalSelect';

/** PLATFORM_ADMIN only: which business the whole app currently shows. Picking
 * one lands on its dashboard; clearing returns to the businesses list. */
export function BusinessSelector({ style }: { style?: CSSProperties }): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { businesses, businessesLoading, setBusiness } = useBusinessContext();
  const current = user?.businessId ?? null;
  const options = businesses.map((b) => ({
    value: b.id,
    label: b.name,
    description: t('businesses.counts', { users: b.counts.users, stores: b.counts.stores }),
  }));
  // A selected business that the list no longer offers (archived meanwhile)
  // still needs a label until /auth/me catches up.
  if (current && user?.business && !options.some((o) => o.value === current)) {
    options.unshift({ value: current, label: user.business.name, description: '' });
  }
  return (
    <ModalSelect
      value={current}
      onChange={(v) => {
        void setBusiness(v ?? null)
          .then(() => navigate(v ? '/' : '/businesses', { replace: true }))
          .catch(() => message.error(t('common.requestError')));
      }}
      loading={businessesLoading}
      allowClear
      size="middle"
      variant="filled"
      title={t('common.business')}
      placeholder={t('common.selectBusiness')}
      style={{ minWidth: 160, maxWidth: 220, ...style }}
      options={options}
    />
  );
}
